/**
 * delineateApi — AK-334 клиент детекции границ поля по точке тапа.
 *
 * Боевой путь: `POST /api/v1/fields/delineate {lng, lat}` (AK-333, SAM
 * point-prompt по спутнику) → `{geometry, area_ha}` за 1–3 сек. Таймаут 10 сек.
 *
 * Dev-фоллбек (ТОЛЬКО за флагом `localStorage.delineateMock='1'`): генерит
 * правдоподобный контур локально (mockPolygon.ts) — чтобы прокликать flow до
 * готовности ML-сервиса. В проде без флага mock не вызывается.
 *
 * Результаты:
 *   - {found:true, geometry, area_ha} — контур найден
 *   - {found:false}                   — сервис ответил, но поле не распознано
 *     (404 / found:false / низкий confidence) → UI показывает тост «не распознано»
 *   - throw DelineateError('timeout' | 'unavailable') — сеть/таймаут/5xx → UI
 *     показывает тот же тост, карта не блокируется
 */
import type { LngLat } from '../editor/fieldGeojson';
import { mockDelineate } from './mockPolygon';

/** Таймаут ожидания детекции, мс (founder scope: 10 сек). */
export const DELINEATE_TIMEOUT_MS = 10_000;

/**
 * GeoJSON контур поля (то, что кладём в редактор / сохраняем). Детект возвращает
 * Polygon; AK-371 — редактор может вернуть MultiPolygon (несмежные части / поле-
 * донат). create_field / PUT geometry принимают оба (ST_Multi канонизирует).
 */
export type DelineateGeometry =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export type DelineateResult =
  | { found: true; geometry: DelineateGeometry; area_ha: number }
  | { found: false };

/** Причина, по которой детекция не удалась (для одинакового тоста в UI). */
export type DelineateErrorKind = 'timeout' | 'unavailable';

export class DelineateError extends Error {
  constructor(public kind: DelineateErrorKind) {
    super(`delineate ${kind}`);
    this.name = 'DelineateError';
  }
}

/** true, если dev-mock включён через localStorage. Безопасно в SSR. */
export function isDelineateMockEnabled(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem('delineateMock') === '1';
  } catch {
    return false;
  }
}

/**
 * Детектировать границы поля по точке [lng, lat].
 *
 * @throws DelineateError при таймауте / недоступности сервиса.
 */
export async function delineateField(point: LngLat): Promise<DelineateResult> {
  const [lng, lat] = point;

  // Dev-mock за флагом — короткая задержка имитирует сеть, затем правдоподобный
  // контур. Не ходит в сеть вообще.
  if (isDelineateMockEnabled()) {
    await sleep(500 + Math.random() * 600);
    const m = mockDelineate([lng, lat]);
    return { found: true, geometry: m.geometry, area_ha: m.area_ha };
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), DELINEATE_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch('/api/v1/fields/delineate', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lng, lat }),
      signal: controller.signal,
    });
  } catch (err) {
    // AbortError (таймаут) vs сетевой сбой — оба ведут в тот же тост, но
    // различаем для диагностики.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new DelineateError('timeout');
    }
    throw new DelineateError('unavailable');
  } finally {
    window.clearTimeout(timer);
  }

  // 404 = поле не распознано (штатный краевой случай, НЕ ошибка сервиса).
  if (res.status === 404) return { found: false };
  if (!res.ok) throw new DelineateError('unavailable');

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new DelineateError('unavailable');
  }
  return parseDelineateBody(body);
}

/** Разбор тела ответа в DelineateResult. Экспортируется для юнит-теста. */
export function parseDelineateBody(body: unknown): DelineateResult {
  if (!body || typeof body !== 'object') return { found: false };
  const b = body as { found?: unknown; geometry?: unknown; area_ha?: unknown };
  // Явный found:false — поле не распознано.
  if (b.found === false) return { found: false };
  const geometry = b.geometry;
  if (
    geometry &&
    typeof geometry === 'object' &&
    (geometry as { type?: unknown }).type === 'Polygon' &&
    Array.isArray((geometry as { coordinates?: unknown }).coordinates)
  ) {
    const area_ha = typeof b.area_ha === 'number' && Number.isFinite(b.area_ha) ? b.area_ha : 0;
    return { found: true, geometry: geometry as DelineateGeometry, area_ha };
  }
  // Нет валидной геометрии → трактуем как «не распознано».
  return { found: false };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}
